import React, { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Icon, List, Text, useTheme } from "@bookingcom/bui-react";
import { useHistory, useLocation } from "react-router-dom";
import { Trans, useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import FlightCardMobile from "../FlightCard/FlightCard.mobile.js";
import FlightCardPositionAnnouncement from "./FlightCardPositionAnnouncement.js";
import { SearchPaginationAutoLoadMobile, SearchPaginationMobile } from "../SearchPagination/index.js";
import { SearchRemoveIcon } from "@bookingcom/bui-assets-react/streamline";
import { SkeletonFlightCard } from "../Skeleton/SkeletonFlightCard.js";
import { TooManyFilters } from "./components/TooManyFilters.js";
import { NoResultsFound } from "./components/NoResultsFound.js";
import { getExpandedSearchAirports } from "../../../store/selectors/expandedSearch.js";
import { FILTERS } from "../../../constants.js";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import SearchBaggagePolicies from "../SearchBaggagePolicies/index.js";
import PriceAlertToggle from "../PriceAlerts/PriceAlertToggle.js";
import { useIsPriceAlertsEligible } from "../PriceAlerts/usePriceAlertToggle.js";
import useClientCachedURL from "../../../hooks/useClientCachedURL.js";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import { SearchLoadingMessage } from "../SearchLoadingMessage/index.js";
import { getLocationParams } from "./utils.js";
import Frame from "../Frame/index.js";
import { FlightCampaignTCBanner } from "../FlightCampaignTCBanner/index.js";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { SurveyTrackerLoader } from "../../remote/SurveyTrackerFlightsUniversal.js";
import { FlexibleDateBestPrice } from "../FlexibleDateBestPrice/index.js";
import styles from "./SearchResults.module.css";
import { useFlexDatesRectrictedPOS } from "../FlexibleDateBestPrice/useFlexDatesRectrictedPOS.js";
import { RemoteAppleCardSR } from "../../remote/index.js";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa.js";
import useOfferCartOrderData from "../../../hooks/useOfferCartOrderData.js";
import useAlternativeFaresParams from "../../../hooks/useAlternativeFaresParams.js";
import useIsInViewport from "../../../hooks/useIsInViewport.js";
import flights_mdotvalue_flight_details_back_button from "../../../utils/experiments/mdotvalue/flights_mdotvalue_flight_details_back_button.js";
import { useStickySearchBoxExp } from "../../../utils/experiments/mdotvalue/flights_mdotvalue_sr_sticky_search_box.js";
import { useTrackSplitBoundsStages } from "../../../utils/experiments/funnel/flights_split_bounds_us.js";
import useTrackTop5Offers from "../../../hooks/useTrackTop5Offers.js";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters.js";
import flights_funnel_sr_auto_load_offers_mobile from "../../../utils/experiments/funnel/flights_funnel_sr_auto_load_offers_mobile.js";
import { shouldShowXMSurvey } from "./utils/shouldShowXMSurvey.js";
import { getSearchResultsVisibleIndexes, getSearchResultsCampaignBanner, getSearchResultsRemoteBanners, getSearchDisplayOptions, getSearchResultsSlice } from "../../../store/searchResults/selectors.js";
import { useSelector } from "react-redux";
import { getSearch, getCabinClass } from "../../../store/search/selectors.js";
import { getFlexDateMinPriceFetchStatus } from "../../../store/flexibleDateMinPrice/selectors.js";
const XM_SURVEY_MIN_RESULTS_FOR_STANDARD_POSITION = 10;
function _SearchResultsList({ flights, onSelectFlight, dataTestId }) {
    const campaignBanner = useSelector(getSearchResultsCampaignBanner);
    const remoteBanners = useSelector(getSearchResultsRemoteBanners);
    const displayOptions = useSelector(getSearchDisplayOptions);
    const cabinClass = useSelector(getCabinClass);
    const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
    const params = new URLSearchParams(location.search);
    const currentSearchResultsPageNumber = params.get("page");
    useTrackSplitBoundsStages();
    const isFirstSearchResultsPage = currentSearchResultsPageNumber ? Number(currentSearchResultsPageNumber) === 1 : true;
    useEffect(() => {
        flights_apex_web_ancillaries_micro_conversion_aa.trackSearchResultsStages(flights);
    }, [flights]);
    const shouldShowFlexDates = !displayOptions?.hideFlexiblePricesBanner &&
        flights.length >= 5 &&
        cabinClass === "ECONOMY" &&
        isFirstSearchResultsPage &&
        !isRestricedFlexDatePOS;
    return (React.createElement("div", { className: styles.searchResultsList, "data-testid": dataTestId },
        React.createElement(List, { attributes: { style: { padding: 0 } } }, flights.map((flight, index) => {
            return (React.createElement(Fragment, { key: `${index}_${flight.token}` },
                campaignBanner && index === campaignBanner.position ? (React.createElement(Frame, null,
                    React.createElement(FlightCampaignTCBanner, { campaignBanner: campaignBanner }))) : null,
                React.createElement(FlightCardPositionAnnouncement, { index: index, total: flights.length }),
                React.createElement("div", { id: `flightcard-${index}`, "data-testid": "searchresults_card" },
                    React.createElement(FlightCardMobile, { flight: flight, index: index, onClick: onSelectFlight, pricePerPerson: true })),
                React.createElement(Frame, null, shouldShowFlexDates && index === 0 ? React.createElement(FlexibleDateBestPrice, null) : null),
                campaignBanner &&
                    campaignBanner.position >= flights.length &&
                    index >= flights.length - 1 ? (React.createElement(Frame, null,
                    React.createElement(FlightCampaignTCBanner, { campaignBanner: campaignBanner }))) : null,
                Array.isArray(remoteBanners) && remoteBanners.includes("APPLE_CARD_BANNER_SR") && index === 4 ? (React.createElement(RemoteAppleCardSR, null)) : undefined,
                shouldShowXMSurvey(index, flights.length, XM_SURVEY_MIN_RESULTS_FOR_STANDARD_POSITION) ? (React.createElement(SurveyTrackerLoader, null)) : null));
        }))));
}
const SearchResultsList = React.memo(_SearchResultsList);
const SearchResults = ({ airports, params, flights = [], fetchStatus, onSelectFlight }) => {
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();
    const { createUrl } = useCreateUrl();
    const search = useSelector(getSearch);
    const searchResults = useSelector(getSearchResultsSlice);
    const flexibleDatePriceFetchStatus = useSelector(getFlexDateMinPriceFetchStatus);
    const cabinClass = useSelector(getCabinClass);
    const { error } = searchResults;
    const appliedFilters = useAppliedFilters();
    const i18n = useI18n();
    const { cacheLastSuccessfulSearchUrl } = useClientCachedURL();
    const { requestId } = useGlobalContext();
    const trackV2 = useEventTracking("search_results", requestId);
    const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
    const offerCartOrderData = useOfferCartOrderData();
    const { setAlternativeFaresParams } = useAlternativeFaresParams();
    const { trackTop5OffersClick } = useTrackTop5Offers();
    const isPriceAlertsEligible = useIsPriceAlertsEligible();
    const { trackReachedEndOfPage } = useStickySearchBoxExp();
    const endOfPageRef = useRef(null);
    const reachedEndOfPage = useIsInViewport(endOfPageRef.current);
    useEffect(() => {
        if (reachedEndOfPage) {
            trackReachedEndOfPage();
        }
    }, [reachedEndOfPage, trackReachedEndOfPage]);
    useEffect(() => {
        if (flightsSessionStore.get("clickedFlightDetails")) {
            flights_mdotvalue_flight_details_back_button.goals.onBackToSr();
            flights_mdotvalue_flight_details_back_button.goals.onFlightExit();
        }
        flightsSessionStore.set("clickedFlightDetails", false);
        if (["loading", "initial"].includes(fetchStatus))
            return;
        if (flights.length === 0)
            trackV2("show_empty_search_results");
    }, [flights, fetchStatus, trackV2]);
    const showFlightDetails = useCallback(async (flight) => {
        trackTop5OffersClick(flight.token);
        onSelectFlight();
        flightsSessionStore.set("clickedFlightDetails", true);
        offerCartOrderData.set(flight.token, {
            offer: flight.token,
            cabinClass: searchResults.searchCriteria?.cabinClass
        });
        history.push(createUrl(`/flights/${airports}/${flight.token}/?${getLocationParams(params)}`), {
            fromSearch: true
        });
    }, [
        onSelectFlight,
        offerCartOrderData,
        searchResults.searchCriteria?.cabinClass,
        history,
        createUrl,
        airports,
        params,
        trackTop5OffersClick
    ]);
    const changeSearch = useCallback(() => {
        history.push(createUrl("/"));
    }, [history, createUrl]);
    const expandedSearchAirports = useMemo(() => {
        const { searchSegments } = search;
        const { from, to } = searchSegments[0];
        return getExpandedSearchAirports(from, to);
    }, [search]);
    const expandToCitySearch = useCallback((e) => {
        e.preventDefault();
        if (expandedSearchAirports) {
            const { fromCities, toCities, fromLocationName, toLocationName } = expandedSearchAirports;
            const params = new URLSearchParams(location.search);
            params.set("from", fromCities);
            params.set("to", toCities);
            params.set("fromLocationName", fromLocationName);
            params.set("toLocationName", toLocationName);
            history.replace(createUrl(`/flights/${fromCities}-${toCities}/?${params}`));
            window.location.reload();
        }
    }, [history, location, expandedSearchAirports, createUrl]);
    const noFlightsFound = error && error["code"] === "SEARCH_SEARCHFLIGHTS_NO_FLIGHTS_FOUND";
    const canExtendSearch = appliedFilters.length === 0 && noFlightsFound && expandedSearchAirports;
    const canRemoveDirectFlightsFilter = appliedFilters.includes(FILTERS.stops) && flights.length === 0;
    const flexibleLoadingCondition = cabinClass === "ECONOMY" && !isRestricedFlexDatePOS
        ? flexibleDatePriceFetchStatus === "initial" || flexibleDatePriceFetchStatus === "loading"
        : false;
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    if ((fetchStatus === "initial" || fetchStatus === "loading" || flexibleLoadingCondition) &&
        !((typeof window !== "undefined" &&
            window.location.search.includes("auto_load_page") &&
            flights.length > 0 &&
            !!flights_funnel_sr_auto_load_offers_mobile.trackWithDefaultStage()))) {
        return (React.createElement(React.Fragment, null,
            React.createElement(SearchLoadingMessage, null),
            React.createElement("div", { className: styles.loading },
                React.createElement(SkeletonFlightCard, null),
                React.createElement(SkeletonFlightCard, null),
                React.createElement(SkeletonFlightCard, null),
                React.createElement(SkeletonFlightCard, null))));
    }
    if (fetchStatus === "success" ||
        (typeof window !== "undefined" &&
            window.location.search.includes("auto_load_page") &&
            flights.length > 0 &&
            !!flights_funnel_sr_auto_load_offers_mobile.trackWithDefaultStage())) {
        if (flights.length > 0) {
            cacheLastSuccessfulSearchUrl(airports);
            searchResults.fetchedAt && setAlternativeFaresParams("searchedAt", searchResults.fetchedAt);
            return (React.createElement("div", { className: styles.searchResults },
                React.createElement(NoDirectFlightsBanner, null),
                React.createElement(SearchBaggagePolicies, null),
                isPriceAlertsEligible && React.createElement(PriceAlertToggle, null),
                React.createElement(SearchResultsList, { flights: searchResultsVisibleIndexes
                        ? flights.filter((_, index) => searchResultsVisibleIndexes.includes(index))
                        : flights, onSelectFlight: showFlightDetails, dataTestId: "searchresults_list" }),
                React.createElement("div", { ref: endOfPageRef }),
                React.createElement(SearchPaginationAutoLoadMobile, null),
                !searchResultsVisibleIndexes || !!flights_funnel_sr_auto_load_offers_mobile.trackWithDefaultStage() ? (React.createElement(SearchPaginationMobile, null)) : undefined));
        }
        else if (canRemoveDirectFlightsFilter) {
            return React.createElement(RemoveDirectFlights, null);
        }
    }
    if (fetchStatus === "failed") {
        if (canExtendSearch) {
            return (React.createElement(Frame, { mt: 6, mb: 6, attributes: { style: { minHeight: "40vh" } } },
                React.createElement(Frame, { m: 3, alignItems: "center", attributes: { style: { textAlign: "center" } } },
                    React.createElement(Icon, { svg: SearchRemoveIcon, size: "small", className: styles.searchRemoveIcon }),
                    React.createElement(Text, { variant: "headline_2" },
                        i18n.trans(t("flights_search_no_results_header")),
                        " ")),
                search.type !== "MULTISTOP" && (React.createElement(Text, { variant: "body_2" },
                    React.createElement(Trans, { tag: "flights_search_no_results_copy_mobile", variables: { start_link: "<0>", end_link: "</0>" }, components: [
                            React.createElement("a", { key: 0, href: "##", style: { color: theme.colors.color_action_foreground }, onClick: expandToCitySearch })
                        ] }))),
                React.createElement(Frame, { alignItems: "center", m: 6 },
                    React.createElement(Button, { onClick: changeSearch }, i18n.trans(t("flights_search_no_results_change_search_cta"))))));
        }
    }
    if (appliedFilters.length > 0) {
        return React.createElement(TooManyFilters, null);
    }
    else {
        return React.createElement(NoResultsFound, { showActions: true });
    }
};
export default React.memo(SearchResults);
